import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Alert, Badge, Button, Card, DataTable, Empty, ErrorBox, FormError, Loading, Modal, PageHeader, SelectInput, Tabs, TextArea, TextInput, useToast } from "../ui";
import { useAuth, useIsOwner } from "../auth";
import { dateTime, date } from "../format";
import { validateReviewUrl } from "../../shared/review";
import { QrCode } from "../review";

export default function Settings() {
  const owner = useIsOwner();
  const [tab, setTab] = useState<"business" | "users" | "accounting" | "integrations" | "audit" | "account" | "monitoring">(owner ? "business" : "account");
  const q = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings") });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const tabs = owner
    ? [{ value: "business", label: "Business" }, { value: "accounting", label: "VAT & periods" }, { value: "users", label: "Users" }, { value: "integrations", label: "Integrations" }, { value: "audit", label: "Audit trail" }, { value: "monitoring", label: "Monitoring" }, { value: "account", label: "My account" }] as const
    : [{ value: "account", label: "My account" }, { value: "integrations", label: "Integrations" }] as const;
  return (
    <>
      <PageHeader title="Settings" />
      <Tabs value={tab} onChange={setTab} tabs={tabs as any} />
      {tab === "business" && <BusinessForm value={q.data.settings.business} invoice={q.data.settings.invoice} />}
      {tab === "accounting" && <AccountingSettings vat={q.data.settings.vat} lockDate={q.data.lockDate} />}
      {tab === "users" && <Users />}
      {tab === "integrations" && <Integrations i={q.data.integrations} />}
      {tab === "audit" && <Audit />}
      {tab === "account" && <><TwoStepCard /><MyAccount /></>}
      {tab === "monitoring" && <Monitoring />}
    </>
  );
}

function useSaveSetting(key: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({ mutationFn: (v: unknown) => api.put(`/settings/${key}`, v), onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast("success", "Settings saved"); } });
}

function BusinessForm({ value, invoice }: { value: any; invoice: any }) {
  const [f, setF] = useState(value);
  const [inv, setInv] = useState(invoice);
  const save = useSaveSetting("business");
  const saveInv = useSaveSetting("invoice");
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="stack">
      <Card title="Business details (printed on new invoices)">
        <div className="form-grid">
          <TextInput label="Business name" value={f.name} onChange={set("name")} />
          <TextInput label="Phone" value={f.phone} onChange={set("phone")} />
          <TextInput label="Address" full value={f.address} onChange={set("address")} />
          <TextInput label="Email" value={f.email} onChange={set("email")} />
          <TextInput label="Website" value={f.website} onChange={set("website")} />
          <TextInput label="Opening hours" value={f.hours} onChange={set("hours")} />
          <TextInput label="Company number" value={f.companyNumber} onChange={set("companyNumber")} />
          <TextArea label="Bank details for transfers" full value={f.bankDetails} onChange={set("bankDetails")} hint="Shown on invoices. Leave blank to hide." />
          <ReviewLinkField value={f.reviewUrl ?? ""} onChange={(v) => setF({ ...f, reviewUrl: v })} />
        </div>
        <div className="form-actions"><Button variant="primary" loading={save.isPending} onClick={() => save.mutate(f)}>Save details</Button></div>
        <FormError error={save.error} />
      </Card>
      <Card title="Invoice options">
        <div className="form-grid">
          <TextInput label="Payment terms (days)" inputMode="numeric" value={String(inv.paymentTermsDays)} onChange={(e) => setInv({ ...inv, paymentTermsDays: Number(e.target.value.replace(/\D/g, "") || 0) })} hint="0 = due on collection" />
          <TextArea label="Footer text" full value={inv.footer} onChange={(e) => setInv({ ...inv, footer: e.target.value })} />
        </div>
        <div className="form-actions"><Button variant="primary" loading={saveInv.isPending} onClick={() => saveInv.mutate(inv)}>Save invoice options</Button></div>
        <FormError error={saveInv.error} />
      </Card>
    </div>
  );
}

/** Google review link: only real review-form links are accepted; the QR preview appears once the link is valid. */
function ReviewLinkField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const v = validateReviewUrl(value);
  return (
    <div className="full stack">
      <TextInput label="Google review link (shown as a QR code on invoices)" value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://g.page/r/…/review"
        error={!v.ok ? v.error : undefined}
        hint="Google Business Profile → “Ask for reviews” (or “Get more reviews”) → copy the link. Leave blank to hide the QR code." />
      {v.ok && v.url && (
        <div className="row" style={{ alignItems: "center" }}>
          <div style={{ width: 120 }}><QrCode text={v.url} label="Preview of the review QR code" /></div>
          <div className="small">
            <div>QR preview — it opens: <span className="mono">{v.url}</span></div>
            <a className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} href={v.url} target="_blank" rel="noopener noreferrer">Open the link to check it shows the review form</a>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountingSettings({ vat, lockDate }: { vat: any; lockDate: string | null }) {
  const [v, setV] = useState(vat);
  const [lock, setLock] = useState(lockDate ?? "");
  const save = useSaveSetting("vat");
  const qc = useQueryClient();
  const toast = useToast();
  const saveLock = useMutation({ mutationFn: () => api.put("/settings/lock/date", { lockDate: lock || null }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast("success", lock ? `Periods locked up to ${date(lock)}` : "Period lock removed"); } });
  return (
    <div className="stack">
      <Card title="VAT">
        {!vat.confirmed && <Alert kind="warn" title="VAT status not confirmed">Last reported status: not VAT registered. Confirm with your accountant before going live.</Alert>}
        <div className="form-grid" style={{ marginTop: 12 }}>
          <SelectInput label="VAT registered?" value={v.registered ? "yes" : "no"} onChange={(e) => setV({ ...v, registered: e.target.value === "yes" })} options={[{ value: "no", label: "No — not VAT registered" }, { value: "yes", label: "Yes — charge VAT" }]} />
          <TextInput label="VAT number" value={v.vatNumber} onChange={(e) => setV({ ...v, vatNumber: e.target.value })} disabled={!v.registered} />
          <TextInput label="Default VAT rate (%)" inputMode="decimal" value={String(v.defaultRateBp / 100)} onChange={(e) => setV({ ...v, defaultRateBp: Math.round(Number(e.target.value || 0) * 100) })} disabled={!v.registered} />
          <label className="check"><input type="checkbox" checked={v.confirmed} onChange={(e) => setV({ ...v, confirmed: e.target.checked })} /> I have confirmed this with the accountant</label>
        </div>
        <Alert kind="info">Changing this only affects quotes and invoices created afterwards. Issued invoices keep the VAT treatment they were issued with.</Alert>
        <div className="form-actions"><Button variant="primary" loading={save.isPending} onClick={() => save.mutate(v)}>Save VAT settings</Button></div>
        <FormError error={save.error} />
      </Card>
      <Card title="Period lock">
        <p className="muted">After the accountant has finalised a period, lock it so nothing dated on or before the lock date can be posted or reversed. Corrections then go into the next open period.</p>
        <div className="row" style={{ alignItems: "flex-end" }}>
          <TextInput label="Locked up to and including" type="date" value={lock} onChange={(e) => setLock(e.target.value)} />
          <Button variant="primary" loading={saveLock.isPending} onClick={() => saveLock.mutate()}>{lock ? "Set lock" : "Remove lock"}</Button>
        </div>
        <FormError error={saveLock.error} />
      </Card>
    </div>
  );
}

function Users() {
  const q = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users") });
  const [adding, setAdding] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const toggle = useMutation({ mutationFn: (u: any) => api.patch(`/users/${u.id}`, { active: !u.active }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast("success", "User updated"); }, onError: (e) => toast("error", (e as Error).message) });
  const reset2 = useMutation({ mutationFn: (u: any) => api.patch(`/users/${u.id}`, { resetTwoStep: true }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast("success", "Two-step sign-in reset. They sign in with their password and can set it up again."); }, onError: (e) => toast("error", (e as Error).message) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <Card title="Users" actions={<Button variant="primary" size="sm" onClick={() => setAdding(true)}>+ Add user</Button>} pad={false}>
      <DataTable rows={q.data.users} columns={[
        { key: "n", label: "Name", render: (u: any) => u.name },
        { key: "e", label: "Email", render: (u: any) => u.email },
        { key: "r", label: "Role", render: (u: any) => <Badge tone={u.role === "owner" ? "navy" : undefined}>{u.role === "owner" ? "Owner" : "Staff"}</Badge> },
        { key: "t", label: "Two-step", render: (u: any) => u.two_step ? <Badge tone="green">On</Badge> : <Badge tone={u.role === "owner" ? "amber" : undefined}>Off</Badge> },
        { key: "l", label: "Last sign-in", render: (u: any) => dateTime(u.last_login_at) },
        { key: "a", label: "", render: (u: any) => u.id !== user?.id && <div className="row">
          {u.two_step ? <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Reset two-step sign-in for ${u.name}? Use this only if they lost their phone and recovery codes.`)) reset2.mutate(u); }}>Reset two-step</Button> : null}
          <Button size="sm" variant={u.active ? "danger" : "secondary"} onClick={() => toggle.mutate(u)}>{u.active ? "Deactivate" : "Reactivate"}</Button>
        </div> },
      ]} />
      <div className="card-body small muted">Staff can run the workshop (jobs, bookings, quotes, invoices, taking payments). Owners also see financial reports, purchase costs, wages and settings.</div>
      {adding && <AddUserModal onClose={() => setAdding(false)} />}
    </Card>
  );
}

function AddUserModal({ onClose }: { onClose: () => void }) {
  const [f, setF] = useState({ name: "", email: "", role: "staff", password: "" });
  const qc = useQueryClient();
  const m = useMutation({ mutationFn: () => api.post("/users", f), onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); onClose(); } });
  return (
    <Modal title="Add user" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Create user</Button></>}>
      <div className="form-grid">
        <TextInput label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <TextInput label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <SelectInput label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} options={[{ value: "staff", label: "Staff" }, { value: "owner", label: "Owner" }]} />
        <TextInput label="Temporary password" type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} hint="At least 10 characters. Ask them to change it." />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

function Integrations({ i }: { i: any }) {
  const row = (name: string, ok: boolean, detail: string) => (
    <tr><td><strong>{name}</strong></td><td>{ok ? <Badge tone="green">Configured</Badge> : <Badge tone="amber">Not configured</Badge>}</td><td className="small">{detail}</td></tr>
  );
  return (
    <Card title="Integrations" pad={false}>
      <div className="table-wrap"><table className="table"><tbody>
        {row("Vehicle registration lookup", i.vehicleLookupConfigured, i.vehicleLookup === "mock" ? "Development mock provider (synthetic data)." : i.vehicleLookup === "dvla_ves" ? "DVLA Vehicle Enquiry Service (make, colour, fuel, engine size, year — no model or images)." : "Manual entry only. DVLA VES registrations are currently closed; a commercial provider needs an account and pricing approval.")}
        {row("Website enquiry key", i.enquirySecretConfigured, "Optional shared secret the WordPress site sends with each enquiry.")}
        {row("AI assistant", i.aiAssistant, "Disabled until configured and approved. Read-only when enabled.")}
        {row("Customer messaging (SMS/email)", false, "Not connected. Nothing is ever sent to customers automatically.")}
      </tbody></table></div>
      <div className="card-body small muted">Environment: <strong>{i.environment}</strong>. Secrets are stored in Cloudflare and never shown here.</div>
    </Card>
  );
}

function Audit() {
  const q = useQuery({ queryKey: ["audit"], queryFn: () => api.get("/settings/audit?limit=300") });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <Card title="Audit trail (latest 300)" pad={false}>
      <DataTable rows={q.data.entries} columns={[
        { key: "t", label: "When", render: (a: any) => <span className="nowrap">{dateTime(a.at)}</span> },
        { key: "u", label: "Who", render: (a: any) => a.user_name ?? <span className="muted">system</span> },
        { key: "e", label: "Record", render: (a: any) => <>{a.entity} <span className="small muted">{a.action}</span></> },
        { key: "s", label: "What", render: (a: any) => a.summary },
      ]} />
    </Card>
  );
}

function MyAccount() {
  const [f, setF] = useState({ current: "", next: "" });
  const toast = useToast();
  const m = useMutation({ mutationFn: () => api.post("/me/password", f), onSuccess: () => { toast("success", "Password changed. Other devices have been signed out."); setF({ current: "", next: "" }); } });
  useEffect(() => {}, []);
  return (
    <Card title="Change password">
      <div className="form-grid">
        <TextInput label="Current password" type="password" autoComplete="current-password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} />
        <TextInput label="New password" type="password" autoComplete="new-password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} hint="At least 10 characters." />
      </div>
      <div className="form-actions"><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Change password</Button></div>
      <FormError error={m.error} />
    </Card>
  );
}

/** Two-step sign-in with an authenticator app (Google Authenticator, Microsoft Authenticator, etc.). */
function TwoStepCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const status = useQuery({ queryKey: ["two-step"], queryFn: () => api.get("/me/two-step") });
  const [stage, setStage] = useState<"idle" | "password" | "scan" | "codes" | "off" | "newcodes">("idle");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const reset = () => { setStage("idle"); setPassword(""); setCode(""); setSetup(null); };
  const done = () => qc.invalidateQueries({ queryKey: ["two-step"] });
  const start = useMutation({ mutationFn: () => api.post<{ secret: string; otpauthUri: string }>("/me/two-step/setup", { password }), onSuccess: (r) => { setSetup(r); setPassword(""); setStage("scan"); } });
  const enable = useMutation({ mutationFn: () => api.post<{ recoveryCodes: string[] }>("/me/two-step/enable", { code }), onSuccess: (r) => { setCodes(r.recoveryCodes); setStage("codes"); setCode(""); done(); } });
  const disable = useMutation({ mutationFn: () => api.post("/me/two-step/disable", { password, code }), onSuccess: () => { toast("success", "Two-step sign-in turned off"); reset(); done(); } });
  const newCodes = useMutation({ mutationFn: () => api.post<{ recoveryCodes: string[] }>("/me/two-step/recovery-codes", { password, code }), onSuccess: (r) => { setCodes(r.recoveryCodes); setStage("codes"); setPassword(""); setCode(""); done(); } });
  if (status.isLoading) return <Loading />;
  const st = status.data;
  return (
    <Card title={<>Two-step sign-in {st?.enabled ? <Badge tone="green">On</Badge> : <Badge tone="amber">Off</Badge>}</>}>
      {!st?.available && <Alert kind="warn">Two-step sign-in is not set up on the server yet.</Alert>}
      {st?.available && stage === "idle" && (st.enabled
        ? <><p>Signing in needs your password and a code from your authenticator app. Recovery codes left: <strong>{st.recoveryCodesLeft}</strong>.</p>
            {st.recoveryCodesLeft <= 3 && <Alert kind="warn">You are running low on recovery codes. Make new ones.</Alert>}
            <div className="row"><Button onClick={() => setStage("newcodes")}>Make new recovery codes</Button><Button variant="ghost" onClick={() => setStage("off")}>Turn off</Button></div></>
        : <><p>Protects the owner account even if someone learns the password: signing in also needs a 6-digit code from an app on your phone (Google Authenticator, Microsoft Authenticator or similar — free).</p>
            <Button variant="primary" onClick={() => setStage("password")}>Set up two-step sign-in</Button></>)}
      {stage === "password" && <div className="stack">
        <TextInput label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <div className="row"><Button variant="primary" loading={start.isPending} disabled={!password} onClick={() => start.mutate()}>Continue</Button><Button variant="ghost" onClick={reset}>Cancel</Button></div>
        <FormError error={start.error} />
      </div>}
      {stage === "scan" && setup && <div className="stack">
        <p><strong>1.</strong> In your authenticator app choose “Add account” and scan this code.</p>
        <div style={{ width: 200, maxWidth: "100%" }}><QrCode text={setup.otpauthUri} label="QR code for your authenticator app" /></div>
        <p className="small muted">Can't scan? Enter this key in the app instead: <code style={{ wordBreak: "break-all" }}>{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</code></p>
        <p><strong>2.</strong> Enter the 6-digit code the app now shows.</p>
        <TextInput label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} />
        <div className="row"><Button variant="primary" loading={enable.isPending} disabled={code.replace(/\s/g, "").length !== 6} onClick={() => enable.mutate()}>Turn on</Button><Button variant="ghost" onClick={reset}>Cancel</Button></div>
        <FormError error={enable.error} />
      </div>}
      {stage === "codes" && <div className="stack">
        <Alert kind="warn" title="Save these recovery codes now">Each code lets you sign in once if you lose your phone. They will not be shown again. Print them or write them down and keep them somewhere safe — not on the phone.</Alert>
        <div className="recovery-codes" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 8, fontFamily: "monospace", fontSize: 16 }}>{codes.map((c) => <div key={c}>{c}</div>)}</div>
        <div className="row"><Button onClick={() => window.print()}>Print</Button><Button variant="primary" onClick={() => { setCodes([]); reset(); toast("success", "Two-step sign-in is on"); }}>I have saved them</Button></div>
      </div>}
      {(stage === "off" || stage === "newcodes") && <div className="stack">
        <p>{stage === "off" ? "To turn off two-step sign-in, enter your password and a current code." : "Make new recovery codes (the old ones stop working). Enter your password and a current code."}</p>
        <div className="form-grid">
          <TextInput label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <TextInput label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
        <div className="row">
          {stage === "off" ? <Button variant="danger" loading={disable.isPending} disabled={!password || !code} onClick={() => disable.mutate()}>Turn off</Button>
            : <Button variant="primary" loading={newCodes.isPending} disabled={!password || !code} onClick={() => newCodes.mutate()}>Make new codes</Button>}
          <Button variant="ghost" onClick={reset}>Cancel</Button>
        </div>
        <FormError error={disable.error ?? newCodes.error} />
      </div>}
    </Card>
  );
}

/** Owner: server errors seen recently (route and error type only) and how alerts work. */
function Monitoring() {
  const q = useQuery({ queryKey: ["server-errors"], queryFn: () => api.get("/settings/errors") });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <Card title="Monitoring" pad={false}>
      <div className="card-body">
        <p>Server errors in the last 24 hours: <strong>{q.data.counts.last24h}</strong> · last 7 days: <strong>{q.data.counts.last7d}</strong></p>
        <p className="small muted">An outside uptime monitor checks <code>/api/health</code> (app, database and file storage) and <code>/api/health/errors</code> (5 or more server errors in 15 minutes) and emails you if either fails. See docs/OPERATIONS.md.</p>
      </div>
      <DataTable rows={q.data.recent} empty={<Empty title="No server errors recorded" />} columns={[
        { key: "a", label: "When", render: (e: any) => dateTime(e.at) },
        { key: "r", label: "Where", render: (e: any) => <code className="small">{e.method} {e.route}</code> },
        { key: "s", label: "Status", render: (e: any) => e.status },
        { key: "m", label: "Error", render: (e: any) => <span className="small">{e.message}</span> },
      ]} />
    </Card>
  );
}
